import React, { useMemo, useState } from "react";
import { Modal, Table, Button, ButtonGroup, Glyphicon, FormGroup, ControlLabel, FormControl, Checkbox } from "react-bootstrap";
import HelpHintButton from "components/HelpHintButton";
import FormControlLanguage from "../../../UI/FormControlLanguage";
import Select from "react-select";
import { limiterTemplates } from "./Limiters";
import cloneDeep from "lodash.clonedeep";
import DeleteConfirmOverlay from "../../../UI/DeleteConfirmOverlay";
import { createClientField, deleteClientField, updateClientField } from "../../../../../api/cp/clientRegistrationFields";
import { useUserContext } from "../../../../context";

const newField = {
    label_en: '',
    label_ru: '',
    name: '',
    max: null,
    min: null,
    regexp: null,
    placeholder_en: '',
    placeholder_ru: '',
    type: 'text',
    options: [],
};

const fieldTypesOptions = [
    { value: 'text', label: 'Строка', limiters: ['stringLength', 'regexp'], dataType: 'text' },
    { value: 'date', label: 'Дата', limiters: ['dateRange'], dataType: 'date' },
    { value: 'time', label: 'Время', limiters: ['timeRange'], dataType: 'time' },
    { value: 'number', label: 'Число', limiters: ['numberRange'], dataType: 'number' },
    { value: 'email', label: 'Электронная почта', limiters: ['regexp'], dataType: 'text' },
    { value: 'tel', label: 'Номер телефона', dataType: 'text' },
    { value: 'select', label: 'Список', limiters: ['options'], dataType: 'select' },
    { value: 'select-other', label: 'Список с опцией "Другое"', limiters: ['options'], dataType: 'select' },
    { value: 'checkbox', label: 'Чекбокс', dataType: 'checkbox' },
    { value: 'konturFocus', label: 'Название организации (с проверкой через КОНТУР Фокус)', dataType: 'text' },
    { value: 'photo', label: 'Фотография участника' },
    { value: 'disclaimer', label: 'Дисклеймер', dataType: 'checkbox' },
];

export default ({ show, defaultDisclaimers, onHide = () => { }, fields = [], setFields = () => { } }) => {
    const { userIsAdmin, userIsSuperAdmin } = useUserContext();

    const [editingField, setEditingField] = useState(null);

    const updateFields = (field) => {
        let _fields = cloneDeep(fields);
        if (_fields.find(_field => field.id === _field.id)) {
            _fields = _fields.map(_field => _field.id === field.id ? ({ ..._field, ...field }) : _field)
        } else {
            _fields = [..._fields, field];
        }
        setFields(_fields)
    }

    const handleDeleteField = async (field) => {
        const _fields = cloneDeep(fields);
        setFields(_fields.filter(({ id }) => id !== field.id));
        try {
            await deleteClientField(field.id)
        } catch (e) {
            console.error(e);
            setFields(_fields)
        }
    }

    return <Modal
        show={show}
        onHide={onHide}
    >
        <Modal.Header closeButton>
            <Modal.Title>Список полей авторизации</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Button onClick={() => setEditingField(newField)}>
                Добавить
            </Button>
            <Table style={{ marginTop: '1rem', fontSize: '1.25rem' }}>
                <thead>
                    <tr>
                        <th>Ключ</th>
                        <th>Название</th>
                        <th>Подсказка</th>
                        <th width={100}></th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map((field) => <tr key={field.id}>
                        <td>{field.name}</td>
                        <td>{field.label_ru}</td>
                        <td>{field.placeholder_ru}</td>
                        <td>{(!!field.clientId || userIsAdmin) &&
                            <ButtonGroup>
                                <Button onClick={() => setEditingField(field)} disabled={defaultDisclaimers.includes(field.name) && !userIsSuperAdmin}>
                                    <Glyphicon glyph="edit" />
                                </Button>
                                <DeleteConfirmOverlay title="Удалить это поле?" buttonText="Удалить" onConfirm={() => handleDeleteField(field)}>
                                    <Button bsStyle="danger" disabled={defaultDisclaimers.includes(field.name) && !userIsSuperAdmin}><Glyphicon glyph="trash" /></Button>
                                </DeleteConfirmOverlay>
                            </ButtonGroup>
                        }</td>
                    </tr>)}
                </tbody>
            </Table>
            <Modal
                show={!!editingField}
                onHide={() => setEditingField(null)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{editingField?.id ? 'Редактирование' : 'Создание'} поля авторизации</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <EditFieldModal editingField={editingField} setEditingField={setEditingField} updateFields={updateFields} />
                </Modal.Body>
            </Modal>
        </Modal.Body>
    </Modal>
}

const EditFieldModal = ({ editingField, setEditingField, updateFields }) => {

    const [isSaving, setSaving] = useState(false);
    const [error, setError] = useState(false);

    const editingFieldType = useMemo(() => fieldTypesOptions.find(({ value }) => editingField?.type === value) || fieldTypesOptions[0], [editingField]);

    const handleFieldType = (type) => {
        const value = type.value;
        const data = {
            max: null,
            min: null,
            regexp: null,
            options: [],
            defaultValue: null,
        };
        data.type = value;
        if (value === 'photo') {
            data.name = 'photo';
            data.label_ru = 'Фотография участника';
        }
        if (value === 'konturFocus') {
            data.name = 'konturFocusOrg'
        }
        setEditingField(field => ({ ...field, ...data }));
    }

    const handleNameInput = (value) => {
        if (editingField.type === 'photo' || editingField.type === 'konturFocus') return;
        setEditingField(field => ({ ...field, name: value }));
        setError(false);
    }

    const handleSave = async () => {

        if (!editingField.name.match(/^[a-z][_a-z0-9]*$/i)) {
            setError(true)
        } else {
            const id = editingField?.id;
            setSaving(true);
            try {
                const field = await (id ? updateClientField(id, editingField) : createClientField(editingField));
                updateFields(field);
                setEditingField(null);
            } catch (e) {
                console.error(e)
            } finally {
                setSaving(false);
            }
        }
    }

    const renderLimiters = () => {
        const limiters = editingFieldType.limiters || [];
        return limiters.map(limiter => limiterTemplates[limiter] ?
            limiterTemplates[limiter]({ key: limiter, field: editingField, setField: (data) => setEditingField(field => ({ ...field, ...data })), isSaving })
            : <></>)
    };

    const renderDefaultValueEditor = () => {
        const dataType = editingFieldType.dataType;

        const updateDefaultValue = (value) => {
            setEditingField(field => ({ ...field, defaultValue: String(value) }));
        }

        if (['text', 'date', 'time', 'number'].includes(dataType)) {
            return <FormControl
                type={dataType}
                value={editingField.defaultValue || ''}
                onChange={(e) => updateDefaultValue(e.target.value)}
                disabled={isSaving}
            />
        } else if (dataType === 'checkbox') {
            const isChecked = editingField.defaultValue === 'true'
            return <Checkbox
                checked={isChecked}
                onChange={(e) => updateDefaultValue(e.target.checked)}
            />
        } else if (dataType === 'select') {
            const options = (editingField.options || []).map(option => ({ value: option.label_ru, label: option.label_ru }));
            const selectValue = options.find(({ value }) => editingField.defaultValue === value) || null;
            return <Select
                value={selectValue}
                onChange={({ value }) => updateDefaultValue(value)}
                options={options}
                isDisabled={isSaving}
            />
        }
    }

    if (!editingField) return null;

    return <>
        <FormGroup style={{ zIndex: 100, position: 'relative' }}>
            <ControlLabel>Тип</ControlLabel>
            <Select
                value={editingFieldType}
                onChange={(type) => handleFieldType(type)}
                options={fieldTypesOptions}
                isDisabled={isSaving}
            />
        </FormGroup>
        <FormGroup >
            <ControlLabel>
                Ключ
                <HelpHintButton>
                    Ключ должен быть уникальным
                </HelpHintButton>
            </ControlLabel>
            <FormControl
                type="text"
                value={editingField.name}
                style={error ? { borderColor: 'red' } : {}}
                onChange={(e) => handleNameInput(e.target.value)}
                disabled={isSaving}
            />
            <span><i>Разрешены только латинские буквы, цифры и знак '_'. Начинаться должно с буквы</i></span>
        </FormGroup>
        <FormGroup >
            <ControlLabel>Название</ControlLabel>
            <FormControlLanguage
                type="text"
                values={{
                    ru: editingField.label_ru || '',
                    en: editingField.label_en || '',
                }}
                onChange={{
                    ru: (e) => setEditingField(field => ({ ...field, label_ru: e.target.value })),
                    en: (e) => setEditingField(field => ({ ...field, label_en: e.target.value })),
                }}
                disabled={isSaving}
            />
        </FormGroup>
        {(editingField.type !== 'photo' && editingField.type !== 'disclaimer') &&
            <FormGroup >
                <ControlLabel>Подсказка</ControlLabel>
                <FormControlLanguage
                    type="text"
                    values={{
                        ru: editingField.placeholder_ru || '',
                        en: editingField.placeholder_en || '',
                    }}
                    onChange={{
                        ru: (e) => setEditingField(field => ({ ...field, placeholder_ru: e.target.value })),
                        en: (e) => setEditingField(field => ({ ...field, placeholder_en: e.target.value })),
                    }}
                    disabled={isSaving}
                />
            </FormGroup>
        }
        {(editingField.type === 'disclaimer') &&
            <FormGroup >
                <ControlLabel>
                    Описание дисклеймера
                    <HelpHintButton>
                        Для того, чтобы добавить ссылку, используйте подсказу под полем ввода
                    </HelpHintButton>
                </ControlLabel>
                <FormControlLanguage
                    type="textarea"
                    rows={4}
                    values={{
                        ru: editingField.description_ru || '',
                        en: editingField.description_en || '',
                    }}
                    onChange={{
                        ru: (e) => setEditingField(field => ({ ...field, description_ru: e.target.value })),
                        en: (e) => setEditingField(field => ({ ...field, description_en: e.target.value })),
                    }}
                    disabled={isSaving}
                />
                <div><i>{'<a href="адрес_страницы" target="_blank">название_ссылки</a>'}</i></div>
                <div><i>{'{{organizationName}}'}</i> - название организации</div>
                <div><i>{'{{organizationInn}}'}</i> - ИНН организации</div>
            </FormGroup>
        }
        {renderLimiters()}
        {editingFieldType.value !== 'photo' &&
            <FormGroup>
                <ControlLabel>Значение по умолчанию</ControlLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                        {renderDefaultValueEditor()}
                    </div>
                    {editingFieldType.dataType !== 'checkbox' &&
                        <Button
                            bsStyle="danger"
                            disabled={isSaving || editingField.defaultValue === null}
                            style={{ flex: 0 }}
                            onClick={() => setEditingField(field => ({ ...field, defaultValue: null }))}
                        >✕
                        </Button>
                    }
                </div>
            </FormGroup>
        }
        <Button bsStyle="success" style={{ marginTop: '1rem', width: '100%' }} onClick={handleSave} >Сохранить</Button>
    </>
}